import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { Box, Typography, InputAdornment, CircularProgress } from '@mui/material';
import { CustomButton } from '../../../../components/CustomButton';
import { CustomSelect } from '../../../../components/CustomSelect';
import { CustomInput } from '../../../../components/CustomInput';
import { NotFoundItems } from '../../../../components/NotFoundItems';
import { ReactComponent as SearchIcon } from '../../../../assets/images/search.svg';
import Ruki from '../../../../assets/images/ruki.png';
import {
  getSearchCustomer,
  supportSearchCustomerResetStatus
} from '../../../../store/support/actions';
import {
  SEARCH_CUSTOMER_BY,
  TYPE_CUSTOMER_BY_PHONE,
  TYPE_CUSTOMER_BY_LC,
  DISABLED_CONTROL_TOOLTIP,
  PERMISSIONS,
} from '../../../../constants/variables';
import { useSearchCustomerBy } from '../../../../hooks/useSearchCustomerBy';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';
import { ExportCustomers } from '../../components/ExportCustomers';

const styleMainInput = {
  display: 'flex',
  background: '#FFFFFF',
  borderRadius: '12px',
  border: '1px solid',
  borderColor: 'action.disabledBackground',
  alignItems: 'center',
  paddingLeft: '12px',
  height: '36px'
};

const styleSelect = {
  '& > div': {
    backgroundColor: 'transparent !important'
  },
  '& fieldset': {
    border: 'none !important'
  }
};

export const Support = () => {
  const [textError, setTextError] = useState('');
  const dispatch = useDispatch();
  const { statusSearchCustomer, fetchingCustomer } = useSelector((state) => state.support);
  const { getParams, redirectToUserCard } = useSearchCustomerBy();
  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const {
    handleSubmit,
    values,
    handleChange,
    isSubmitting,
    setFieldValue,
    errors,
    setErrors,
    isValid
  } = useFormik({
    initialValues: {
      by: SEARCH_CUSTOMER_BY[0],
      search_type: ''
    },
    validationSchema: yup.object({
      mobile_phoneOrIIN: yup.string(),
      by: yup.object(),
      search_type: yup.object().when('by', {
        is: (val) => val.value !== 'INN',
        then: yup.object().required(textError)
      })
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      if (isSubmitting)
        dispatch(getSearchCustomer(getParams(values)))
          .then(res => res.status === 200 && redirectToUserCard(getParams(values), res.data?.personal_accounts[0].id));
    }
  });

  useEffect(() => {
    values.by.value === SEARCH_CUSTOMER_BY[1].value &&
      setTextError('Для поиска укажите тип абонента');
    values.by.value === SEARCH_CUSTOMER_BY[2].value &&
      setTextError('Для поиска абонента укажите филиал');
    values.search_type && setFieldValue('search_type', '');
    dispatch(supportSearchCustomerResetStatus());
    setErrors({});
  }, // eslint-disable-next-line
    [values.by]);

  useEffect(() => {
    setErrors({})
  }, // eslint-disable-next-line
    [values.search_type]);

  const fields = (name) => ({
    name: name,
    value: values[name],
    error: errors[name],
    helperText: errors[name],
    onChange: handleChange,
    size: 'small',
    variant: 'outlined'
  });

  const checkBySearch = values.by.value !== SEARCH_CUSTOMER_BY[0].value;

  const isDisabledControl = visibleBySectionPermission(permissions, [PERMISSIONS.SUPPORT.ListSupportBonusRequests.value, PERMISSIONS.SUPPORT.RetrieveCustomer.value], 'not');

  const pagePermissions = [PERMISSIONS.SUPPORT.RetrieveCustomer.value, PERMISSIONS.SUPPORT.ListSupportBonusRequests.value];
  const isPageVisible = visibleBySectionPermission(permissions, pagePermissions);

  if (!isPageVisible) {
    return <></>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: '24px', alignItems: 'center' }} mb="24px">
        <Typography variant="h1" sx={{ color: 'text.primary' }} component="h1">
          Техподдержка
        </Typography>
        <ExportCustomers />
      </Box>
      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: checkBySearch ? '3fr 240px' : '1fr',
            gap: '24px'
          }}>
          <Box sx={{ ...styleMainInput }}>
            <CustomSelect
              {...fields('by')}
              setFieldValue={setFieldValue}
              sx={{ ...styleSelect, width: '265px' }}
              options={SEARCH_CUSTOMER_BY}
              startAdornmentText={
                <InputAdornment
                  position="start"
                  sx={{
                    marginRight: 0
                  }}>
                  <Typography component="span" variant="body1" color="text.primary">
                    Искать по:
                  </Typography>
                </InputAdornment>
              }
              disabled={isDisabledControl}
              tooltip={isDisabledControl ? DISABLED_CONTROL_TOOLTIP : null}
            />

            <Box sx={{ borderLeft: '1px solid', borderColor: 'action.disabled', height: '24px' }} />

            <CustomInput
              {...fields('mobile_phoneOrIIN')}
              placeholder="Поиск"
              sx={{ ...styleSelect, width: '100%' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              disabled={isDisabledControl}
              tooltip={isDisabledControl ? DISABLED_CONTROL_TOOLTIP : null}
            />
          </Box>

          {checkBySearch && (
            <CustomSelect
              {...fields('search_type')}
              setFieldValue={setFieldValue}
              placeholder={
                values.by.value === SEARCH_CUSTOMER_BY[1].value
                  ? 'Тип абонента'
                  : 'Идентификатор филиала'
              }
              options={
                values.by.value === SEARCH_CUSTOMER_BY[1].value
                  ? TYPE_CUSTOMER_BY_PHONE
                  : TYPE_CUSTOMER_BY_LC
              }
            />
          )}
        </Box>

        <Box display="flex" justifyContent="center" marginTop="24px">
          <CustomButton
            sx={{ width: '148px' }}
            size="large"
            variant="contained"
            type="submit"
            disabled={!isValid || fetchingCustomer || isDisabledControl}
            tooltip={isDisabledControl ? DISABLED_CONTROL_TOOLTIP : null}
          >
            {fetchingCustomer ? <CircularProgress color="secondary" size={24} data-testid="CircularProgress" /> : 'Найти'}
          </CustomButton>
        </Box>
      </form>

      {statusSearchCustomer ? (
        <NotFoundItems />
      ) : (
        <Box display="flex" justifyContent="center">
          <img src={Ruki} alt="Ruki " />
        </Box>
      )}
    </Box>
  );
};
